import { ModalId, ModalViewAction } from '@heidi/schema/ml-scribe';
import { useCallback } from 'react';

import type { RemoteIntroPromoHost } from './remote-intro-promo-host.types';

/**
 * Backend-tracked "seen" state for the Heidi Remote intro promo (GRO-2981).
 *
 * A single `ModalId.REMOTE_INTRO_PROMO` covers all three surfaces — Billing
 * banner, avatar-dropdown menu card, and the shared modal — because the user
 * resolves to exactly one cohort and we always want them to see one variant
 * of the promo at a time. Dismissing or engaging from any surface marks the
 * whole promo as seen, so the other surfaces hide on the next render.
 *
 * Both writers optimistically push the ID into `seenModalIdsAtom` before the
 * mutation resolves, so the consuming surfaces hide immediately rather than
 * waiting a round-trip.
 */
export function useRemoteIntroPromoSeen(host: RemoteIntroPromoHost) {
  const seenModalIds = host.useSeenModalIds();
  const setSeenModalIds = host.useSetSeenModalIds();
  const { mutate: markModalAsViewed } = host.useMutateMarkModalAsViewed();

  const isSeen = seenModalIds?.has(ModalId.REMOTE_INTRO_PROMO) ?? false;

  const markDismissed = useCallback(() => {
    setSeenModalIds((prev) => new Set([...(prev ?? []), ModalId.REMOTE_INTRO_PROMO]));
    markModalAsViewed({
      modal_id: ModalId.REMOTE_INTRO_PROMO,
      increment_count: true,
      action: ModalViewAction.dismiss,
    });
  }, [markModalAsViewed, setSeenModalIds]);

  const markEngaged = useCallback(() => {
    setSeenModalIds((prev) => new Set([...(prev ?? []), ModalId.REMOTE_INTRO_PROMO]));
    markModalAsViewed({
      modal_id: ModalId.REMOTE_INTRO_PROMO,
      increment_count: true,
      action: ModalViewAction.engage,
    });
  }, [markModalAsViewed, setSeenModalIds]);

  return { isSeen, markDismissed, markEngaged };
}
